import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { VortexHub } from '../../vortex/VortexHub.js';
import { VortexHubRenderer } from '../../vortex/VortexHubRenderer.js';
import { PendingDelete } from '../../vortex/VortexHubInput.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

export interface VortexHubTutorialState {
    /** Serialized VortexHub state (compact format). Empty string = no gates. */
    hubData: string;
    /** Position strings for a simulated build path (e.g. ['E0', 'E1', 'E2']). */
    buildPath?: string[];
    /** Per-frame override: gate keys to render as "used" (filled). */
    usedGateKeys?: string[];
    /** Per-frame override: gate pair to render as pending deletion (red). */
    pendingDelete?: { clickedGateKey: string; pairedGateKey: string };
    /** Per-frame override: a single cell shown as a half-placed gate (pending its pair). */
    pendingCreate?: string;
}

export interface VortexHubTutorialRenderOptions {
    mode: 'none' | 'idle' | 'gates' | 'gates-pending';
    /** Gate keys to render as "used" (filled) in gates mode. */
    usedGateKeys?: string[];
    /** Gate pair to render as pending deletion (red) in gates-pending mode. */
    pendingDelete?: { clickedGateKey: string; pairedGateKey: string };
}

class VortexHubTutorialAdapterImpl implements TutorialGameAdapter {
    private renderer: VortexHubRenderer;
    private hub: VortexHub;
    private grid: Grid;
    /** Per-frame render overrides set by applyState, consumed by render. */
    private frameUsedGateKeys: string[] | undefined;
    private framePendingDelete: { clickedGateKey: string; pairedGateKey: string } | undefined;
    private framePendingCreate: Position | undefined;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.hub = new VortexHub(grid);
        this.renderer = new VortexHubRenderer(canvas, grid);
    }

    applyState(state: unknown): void {
        const s = state as VortexHubTutorialState | null;
        this.hub = new VortexHub(this.grid);
        if (s?.hubData) {
            this.hub.deserialize(s.hubData);
        }
        if (s?.buildPath && s.buildPath.length >= 1) {
            const gs = this.grid.serializer;
            const positions = s.buildPath.map(p => gs.deserializePosition(p));
            this.hub.seedBuildPath(positions);
        }
        // Store per-frame render overrides, converting serialized positions to positionKey format
        const gs = this.grid.serializer;
        const toKey = (p: string) => positionKey(gs.deserializePosition(p));
        this.frameUsedGateKeys = s?.usedGateKeys?.map(toKey);
        this.framePendingDelete = s?.pendingDelete
            ? { clickedGateKey: toKey(s.pendingDelete.clickedGateKey), pairedGateKey: toKey(s.pendingDelete.pairedGateKey) }
            : undefined;
        this.framePendingCreate = s?.pendingCreate ? gs.deserializePosition(s.pendingCreate) : undefined;
    }

    render(options: unknown): void {
        const opts = options as VortexHubTutorialRenderOptions | null;
        const mode = opts?.mode ?? 'idle';
        const gatesMode = mode === 'gates' || mode === 'gates-pending';

        // Per-frame overrides take precedence over scene-level options.
        // frameUsedGateKeys/framePendingDelete are already converted to positionKey format.
        const gs = this.grid.serializer;
        const toKey = (p: string) => positionKey(gs.deserializePosition(p));

        let usedGateKeys: Set<string> | undefined;
        if (this.frameUsedGateKeys) {
            usedGateKeys = new Set(this.frameUsedGateKeys);
        } else if (opts?.usedGateKeys) {
            usedGateKeys = new Set(opts.usedGateKeys.map(toKey));
        }

        let pendingDelete: PendingDelete | undefined;
        const pd = this.framePendingDelete ?? (opts?.pendingDelete
            ? { clickedGateKey: toKey(opts.pendingDelete.clickedGateKey), pairedGateKey: toKey(opts.pendingDelete.pairedGateKey) }
            : undefined);
        if (pd) {
            pendingDelete = {
                clickedGateKey: pd.clickedGateKey,
                pairedGateKey: pd.pairedGateKey,
                usage: { gamesUsing: [], gamesUsingInBest: [], affectedCount: 2, invalidatedWins: 0 },
            };
        }

        // pendingCreate (a half-placed first gate) only renders in the non-gates
        // path, which is exactly where scene 3's create animation runs.
        this.renderer.render(this.hub, gatesMode, {
            pendingDelete,
            usedGateKeys,
            pendingCreate: this.framePendingCreate ?? null,
        });
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createVortexHubTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new VortexHubTutorialAdapterImpl(canvas, grid);
}
